import { useAuthStoreUser } from "../../stores/app/authUser";
import { usePermissoesStore } from "../../stores/app/permissoesStore";
import {
  IdAtendimento,
  IdBot,
  IdCanal,
  IdConta,
  IdContato,
  IdInbox,
  IdQuadro,
  IdUsuario,
} from "../common-types";
import { ResponseContato } from "../contatos/contatos";
import { ResponseEtiqueta } from "../etiquetas/etiquetas.service";
import { GenericService } from "../generic-service";
import { MessageContent, WhizMessageTipo, WhizMessageSituacao } from "../mensagens/mensagens.service";
import { ResponseUsuario } from "../usuarios/usuarios.service";

export type CreateAtendimento = {
    idInbox  : IdInbox,
    idContato: IdContato,
    idCanal  ?: IdCanal,
    avaliacao?: number,
    iniciar: boolean,
}

export type UpdateAtendimento = Partial<CreateAtendimento> & { id: IdAtendimento, idQuadro: IdQuadro|null }

export type ResponseAtendimento = {
    id             : IdAtendimento,
    idInbox        : IdInbox,
    idContato      : IdContato,
    idCanal        : IdCanal|null,
    dataCriacao    : string,
    dataFinalizacao: string|null,
    dataAbertura   : string|null,
    avaliacao      : number|null,

    idBot    : IdBot|null,
    idBotAcao: string|null,
    idQuadro : IdQuadro|null,

    ultimaInteracaoCliente?: string|null // datetime da última interação
    ultimaInteracao?: string|null,    // datetime da última interação
    ultimaMensagem?: string|null,     //conteúdo ou tipo da última mensagem
    ultimoTipoMensagem?: WhizMessageTipo|null, // tipo da última mensagem
    ultimaMensagemInfo?: WhizLastMessageInfo|null,

    atendentes?: ResponseUsuario[]|null,
    contato: {
        id: IdContato,
        nome: string,
        codigo: string,
        idConta: IdConta,
        dataCriacao?: string,
        tipo: string,
        imagem?: string,
        etiquetas?: ResponseEtiqueta[]
    }

    naoLidas?: number|null,
    marcadoNaoLido?: boolean,

    idChatBotIa?: string|null,
    idContaBotIa?: string|null,
    statusIntegracaoIa?: 'EM_ATENDIMENTO_HUMANO'|null,

    /**
     * Status cru do servidor. Não confundir com `Chat.status` (`ChatStatus`), que é
     * derivado das datas em `MakeChat`: só aqui aparece `CRIADO_POR_DISPARO`, e é ele
     * que decide se o atendimento fica oculto na lista.
     */
    status?: AtendimentoStatusServidor,

    /**
     * Vínculo mais recente com uma campanha (`Disparo` no servidor). Nulo quando
     * nenhuma campanha alcançou este atendimento.
     */
    disparo?: AtendimentoDisparo|null,
}

export type AtendimentoStatusServidor =
    | 'CRIADO'
    | 'CRIADO_POR_DISPARO'
    | 'ABERTO'
    | 'FINALIZADO'
    | 'FINALIZADO_AUTOMATICAMENTE'

export type DisparoCreationStatus = 'CRIADO_POR_DISPARO' | 'TOCADO_POR_DISPARO'
export type DisparoReplyStatus = 'ENVIADA_POR_NOS' | 'RECEBIDA_DO_CONTATO' | 'DISPARO_PARA_GRUPO'

export type AtendimentoDisparo = {
    idDisparo: string,
    idAgendamento: string,
    creationStatus: DisparoCreationStatus,
    /** Nulo enquanto não houver nenhuma mensagem depois do envio da campanha. */
    replyStatus: DisparoReplyStatus|null,
}

export type WhizLastMessageInfo ={
    conteudo: MessageContent,
    tipo: WhizMessageTipo,
    enviadaPorMim: boolean,
    situacao?: WhizMessageSituacao
}

export type FullInfoAtendimento = ResponseAtendimento & {
    atendentes: ResponseUsuario[],
    contato: ResponseContato,
    isPinned?: boolean,
}

export type StatusAtendimento = 'ABERTO'|'INICIADO'|'FINALIZADO'

/**
 * Modo de visibilidade aplicado a um grupo de inboxes:
 *   LIVRE = sem restrição | CANAL = restrito por canal/atendente | FILA = próprios + criados (piscando)
 */
type ModoVisibilidade = 'LIVRE' | 'CANAL' | 'FILA'

export type FiltroAtendimentos = {
    idsInboxes   ?: IdInbox[]
    idsAtendentes?: IdUsuario[]
    idsContatos  ?: IdContato[]
    status       ?: StatusAtendimento[]
}

/** Forma enxuta de `GET /atendimentos/abertos-por-contatos`. */
export type AtendimentoAbertoPorContato = {
    idContato : IdContato,
    idInbox   : IdInbox,
    atendentes: { id: IdUsuario, nome: string }[],
    isBot     : boolean,
};

export class AtendimentosService extends GenericService<CreateAtendimento, UpdateAtendimento, ResponseAtendimento> {
    constructor() { super('atendimentos') }

    async findAbertosByContato(idContato: IdContato) {
      return await this.f.get(`/find-abertos-by/contato/${idContato}`) as ResponseAtendimento[];
    }

    /**
     * Atendimentos abertos de vários contatos (inbox + atendentes), recortados na
     * conta — inclusive os de OUTROS atendentes, que a lista do usuário não traz
     * sem `inbox.ver_todos_atendimentos`.
     *
     * Compat: backend antigo não tem a rota em lote → uma chamada por contato na
     * rota legada, reduzida à mesma forma.
     */
    async findAbertosPorContatos(idConta: IdConta, idContatos: IdContato[]): Promise<AtendimentoAbertoPorContato[]> {
      if (!idContatos.length) return [];

      const res = await this.f.get('/abertos-por-contatos', { idConta, idContatos: idContatos.join(',') }, { resolve404AsValue: true, resolve404As: null });
      if (res != null) return res as AtendimentoAbertoPorContato[];

      const porContato = await Promise.all(idContatos.map(id => this.findAbertosByContato(id).catch(() => [] as ResponseAtendimento[])));
      return porContato.flat().map(att => ({
        idContato: att.idContato,
        idInbox: att.idInbox,
        atendentes: (att.atendentes ?? []).map(u => ({ id: u.id, nome: u.nome })),
        isBot: !!att.idBot || (!!att.idChatBotIa && att.statusIntegracaoIa !== 'EM_ATENDIMENTO_HUMANO'),
      }));
    }

    async getAllFullInfo(filtro: FiltroAtendimentos | null, inboxesComCanais?: Set<IdInbox>) {
      const buckets = this.aplicarFiltroPermissaoInbox(filtro, inboxesComCanais);

      const resultados = await Promise.all(
        buckets.map(async ({ filtro: f, modo }) => {
          const atendimentos = await this.buscarAtendimentos(f);
          return modo === 'LIVRE' ? atendimentos : this.aplicarFiltroVisibilidade(atendimentos, modo);
        }),
      );

      return resultados.flat();
    }

    /**
     * Filtra client-side os atendimentos de inboxes restritos (sem ver_todos_atendimentos).
     * Mantém o atendimento se:
     *   - o usuário é atendente, OU
     *   - o canal tem visualização global habilitada para o usuário (vê tudo do canal), OU
     *   - é conduzido pelo Agente de IA, sem atendente, e o usuário tem
     *     `inbox.acompanhar_atendimentos_agente` no inbox, OU
     *   - modo CANAL (inbox com canais): não há atendentes E o canal pertence ao usuário, OU
     *   - modo FILA (inbox sem canais): atendimento ainda não iniciado ("piscando"), sem atendentes
     */
    private aplicarFiltroVisibilidade(
      atendimentos: FullInfoAtendimento[],
      modo: 'CANAL' | 'FILA',
    ): FullInfoAtendimento[] {
      const authStore = useAuthStoreUser();
      const permissoesStore = usePermissoesStore();
      const idUsuario = authStore.user?.id;
      const idConta = authStore.currentConta?.id;
      if (!idUsuario) return atendimentos;

      /**
       * Atendimento que a IA conduz sozinha, sem nenhum humano vinculado.
       *
       * Sem este recorte ele sumia da aba "Atendimentos com IA" para quem não
       * tem `ver_todos_atendimentos`: no modo FILA o teste é
       * `semAtendentes && !dataAbertura`, e o agente ABRE o atendimento ao
       * assumi-lo — então `dataAbertura` vem preenchido e a linha caía fora. No
       * modo CANAL dependia de o atendimento ter caído num canal do usuário,
       * que é justamente o que a IA não faz.
       *
       * O gate é `inbox.acompanhar_atendimentos_agente` (escopo inbox, por isso
       * a checagem usa o inbox DO atendimento): a permissão existe exatamente
       * para deixar acompanhar o que o agente conduz, então liberar sem ela
       * alargaria visibilidade sem ninguém ter pedido.
       */
      const podeAcompanharAgente = (a: FullInfoAtendimento) => {
        const conduzidoPorIa = !!a.idChatBotIa && !a.statusIntegracaoIa;
        if (!conduzidoPorIa || !idConta || !a.idInbox) return false;
        return permissoesStore.temPermissao(
          'inbox.acompanhar_atendimentos_agente',
          idConta,
          a.idInbox,
        );
      };

      return atendimentos.filter(a => {
        if (a.atendentes?.some(at => at.id === idUsuario)) return true;
        // Canal com visão global → vê todos os atendimentos do canal, independente do atendente
        if (a.idCanal != null && authStore.canaisVisualizacaoGlobal[a.idCanal] === true) return true;
        const semAtendentes = !a.atendentes || a.atendentes.length === 0;
        // Conduzido pela IA e sem humano vinculado: visível para quem pode acompanhar
        if (semAtendentes && podeAcompanharAgente(a)) return true;
        // Inbox sem canais: só a fila de "criados" (piscando) fica visível para todos
        if (modo === 'FILA') return semAtendentes && !a.dataAbertura;
        // Canal null = ainda não escolheu → esconde (inbox restrito sempre tem canais)
        return semAtendentes && a.idCanal != null && authStore.canais[a.idCanal] === true;
      });
    }

//     async getAllFullInfo(filtro: FiltroAtendimentos|null) {
//         const arrayToString = (name: string, array: unknown[]) => array.map(el => `${name}=${String(el)}`).join('&')
//         const queryParams = [];
//             if (filtro?.idsAtendentes?.length)  queryParams.push(arrayToString('idsAtendentes', filtro.idsAtendentes));
//             if (filtro?.idsContatos?.length)    queryParams.push(arrayToString('idsContatos', filtro.idsContatos));
//             if (filtro?.idsInboxes?.length)     queryParams.push(arrayToString('idsInboxes', filtro.idsInboxes));
//             if (filtro?.status?.length)         queryParams.push(arrayToString('status',filtro.status));
//         const queryString = queryParams.length ? `?${queryParams.join('&')}` : ''
//         const fullAtendimentos = await this.f.get('/atendentes-contatos/usuario' + queryString) as FullInfoAtendimento[]
//         fullAtendimentos.forEach(el => el.contato.imagem?.includes('pps.whatsapp.net') ? el.contato.imagem = undefined : null)
//         return fullAtendimentos;

//     }

    private async buscarAtendimentos(filtro: FiltroAtendimentos | null) {
      const arrayToString = (name: string, array: unknown[]) =>
        array.map((el) => `${name}=${String(el)}`).join("&");
      const queryParams = [];
      if (filtro?.idsAtendentes?.length)
        queryParams.push(arrayToString("idsAtendentes", filtro.idsAtendentes));
      if (filtro?.idsContatos?.length)
        queryParams.push(arrayToString("idsContatos", filtro.idsContatos));
      if (filtro?.idsInboxes?.length)
        queryParams.push(arrayToString("idsInboxes", filtro.idsInboxes));
      if (filtro?.status?.length)
        queryParams.push(arrayToString("status", filtro.status));
      const queryString = queryParams.length ? `?${queryParams.join("&")}` : "";
      return (await this.f.get(
        "/atendentes-contatos/usuario" + queryString,
      )) as FullInfoAtendimento[];
    }

    async iniciarAtendimento(idAtendimento: string, body?: { emLote?: boolean }) {
        return await this.f.patch(`/${idAtendimento}/iniciar`, body ?? {}) as ResponseAtendimento
    }
    async restaurarAtendimento(idAtendimento: string) {
        return await this.f.patch(`/${idAtendimento}/restaurar`) as ResponseAtendimento
    }
    async transferirAtendimento(idAtendimento: string, idNovoAtendente: string, idCanal: string|null, body?: { emLote?: boolean; tornarAtendenteFixo?: boolean }) {
        return await this.f.patch(`/${idAtendimento}/transferir`, { idUsuario: idNovoAtendente, idCanal, ...body }) as ResponseAtendimento
    }
    async finalizarAtendimento(idAtendimento: string, payload?: { conversao?: boolean; conversaoValor?: number; emLote?: boolean }) {
      const body = payload != null && typeof payload === 'object' ? payload : {};
      return await this.f.patch(`/${idAtendimento}/finalizar`, body) as ResponseAtendimento
    }
    async dispararBotNoAtendimento(idAtendimento: string, idBot: string) {
        return await this.f.patch(`/${idAtendimento}/disparar-bot`, {idBot}) as ResponseAtendimento
    }
    async marcarAtendimentoComoLido(idAtendimento: string) {
        return await this.f.patch(`/${idAtendimento}/leitura`) as ResponseAtendimento
    }
    async marcarAtendimentoComoNaoLido(idAtendimento: string) {
        return await this.f.patch(`/${idAtendimento}/nao-lida`) as ResponseAtendimento
    }

    async patchIdQuadroEAtendentes(idAtendimento: string, idQuadro: string|null, atendentes: (null|string)[]|null, enviarMensagemMovimentacao: boolean = false) {
        return await this.f.patch(`/${idAtendimento}/quadro-e-atendentes`, { idQuadro, atendentes, enviarMensagemMovimentacao }) as void;
    }

    async interromperBot(idAtendimento: string) {
        return await this.f.patch(`/${idAtendimento}/interromper-bot`) as ResponseAtendimento;
  }

  /**
   * Regra de visibilidade dos atendimentos:
   *   - Com ver_todos_atendimentos → bucket LIVRE (sem filtro de atendente)
   *   - Sem ver_todos_atendimentos + inbox COM canais → bucket CANAL:
   *       busca sem filtro de atendente mas aplica filtro client-side
   *       (mostra se usuário é atendente OU se atendimento sem atendente está no canal do usuário)
   *   - Sem ver_todos_atendimentos + inbox SEM canais → bucket FILA:
   *       mostra apenas os próprios atendimentos e os "criados" (piscando, sem atendente)
   *
   * O recorte só é aplicado quando o chamador informa `inboxesComCanais` (lista de chats).
   * Sem esse mapa não há como distinguir FILA de CANAL, e chamadores como o histórico do
   * contato (AttendanceReportModal) aplicam o próprio gate por ver_historico_atendimentos.
   */
  private aplicarFiltroPermissaoInbox(
    filtro: FiltroAtendimentos | null,
    inboxesComCanais?: Set<IdInbox>,
  ): { filtro: FiltroAtendimentos | null; modo: ModoVisibilidade }[] {
    // Chamador não informou quais inboxes têm canais → sem recorte (ver doc acima)
    if (!inboxesComCanais) return [{ filtro, modo: 'LIVRE' }];

    const permissoesStore = usePermissoesStore();
    const authStore = useAuthStoreUser();

    const idConta = authStore.currentConta?.id;
    const idUsuario = authStore.user?.id;

    if (!idConta || !idUsuario) {
      console.warn("[AtendimentosService] Sem conta ou usuário ativo");
      return [{ filtro, modo: 'LIVRE' }];
    }

    const inboxesDisponiveis =
      permissoesStore.minhasPermissoes[idConta]?.inboxes || {};
    const todosIdsInboxes = Object.keys(inboxesDisponiveis);

    const inboxesParaVerificar =
      filtro?.idsInboxes && filtro.idsInboxes.length > 0
        ? filtro.idsInboxes
        : todosIdsInboxes;

    // Um bucket por modo de visibilidade; cada bucket é uma chamada separada à API
    const inboxesPorModo: Record<ModoVisibilidade, IdInbox[]> = {
      LIVRE: [],
      CANAL: [],
      FILA: [],
    };

    inboxesParaVerificar.forEach((idInbox) => {
      const temPermissao = permissoesStore.temPermissao(
        "inbox.ver_todos_atendimentos",
        idConta,
        idInbox,
      );

      if (temPermissao) {
        inboxesPorModo.LIVRE.push(idInbox as IdInbox);
      } else if (inboxesComCanais.has(idInbox as IdInbox)) {
        // Sem permissão + inbox com canais → filtro client-side por canal/atendente
        inboxesPorModo.CANAL.push(idInbox as IdInbox);
      } else {
        // Sem permissão + inbox sem canais → só próprios + criados (piscando)
        inboxesPorModo.FILA.push(idInbox as IdInbox);
      }
    });

    const buckets = (Object.keys(inboxesPorModo) as ModoVisibilidade[])
      .filter((modo) => inboxesPorModo[modo].length > 0)
      .map((modo) => ({
        filtro: { ...filtro, idsInboxes: inboxesPorModo[modo], idsAtendentes: filtro?.idsAtendentes },
        modo,
      }));

    // Nenhum inbox conhecido (ex: permissões ainda não carregadas) → mantém o filtro original
    return buckets.length > 0 ? buckets : [{ filtro, modo: 'LIVRE' }];
  }

  async pinAtendimento(idAtendimento: IdAtendimento): Promise<void> {
    await this.f.post(`/${idAtendimento}/pin`, {});
  }

  async unpinAtendimento(idAtendimento: IdAtendimento): Promise<void> {
    await this.f.delete(`/${idAtendimento}/pin`);
  }

  async getDistribuicaoPorCanal(params: {
    idsCanais: string[];
    dateStart: string;
    dateEnd: string;
    page: number;
    limit?: number;
  }) {
      const { idsCanais, dateStart, dateEnd, page, limit = 50 } = params;
      const query = [
          ...idsCanais.map(id => `idsCanais=${id}`),
          `dateStart=${dateStart}`,
          `dateEnd=${dateEnd}`,
          `page=${page}`,
          `limit=${limit}`,
      ].join('&');
      return (await this.f.get(`/distribuicao-canal?${query}`)) as {
          items: ItemDistribuicaoCanal[];
          total: number;
          page: number;
          limit: number;
          hasMore: boolean;
      };
  }
}

/** Atendente fora de turno no instante de uma execução do rodízio por horário. */
export type IndisponivelRodizio = {
    idUsuario: string;
    nome: string;
    /** `MotivoAusencia` de rodizio-logic. */
    motivo: string;
    /** "HH:MM" local do inbox em que a condição termina, quando se sabe. */
    ate: string | null;
};

/** Linha do relatório de rodízio. Espelha `ItemDistribuicaoCanal` do whiz-server. */
export type ItemDistribuicaoCanal = {
    id: string;
    contactName: string;
    agentName: string;
    date: string;
    /** Retrato da última execução do rodízio; nulo = não passou por ele, ou é anterior ao registro. */
    rodizio: {
        tipo: 'NORMAL' | 'RESPEITA_JORNADA_TRABALHO';
        fallback: boolean;
        data: string;
        /** Só na política por horário; nulo no rodízio normal. */
        indisponiveis: IndisponivelRodizio[] | null;
    } | null;
};
